// Catalog.ID Client — Organization Key Access
// ============================================================================
// A delegate never holds the organization's private keys directly: it holds
// them WRAPPED to its own attr_encrypt key (§3a.3.1, `/api/delegate/wrappedkeys`).
// Anything addressed to the ORG — org claims, and since the operator↔org
// binding landed, publicclaim review keys (operator-org-binding plan §2.7) —
// must therefore be unwrapped through the delegate's own key first.
//
// This module is the single implementation of that unwrap. Three panes grew
// their own copies of it; the copies disagreed about key ordering, which is
// only observable after an org key rotation (see `getOrgAttrKey` below).

import * as delegateApi from '../api/delegate';
import { unwrapPrivateKey } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from './keys';

let cachedChain: { keyIndex: number; secretKey: Uint8Array }[] | null = null;

/**
 * Every org `attr_encrypt` private key the current delegate can unwrap,
 * ascending by keyIndex — the chain `unwrapSymmetricKeyChain` walks to peel a
 * post-rotation claim key. Empty when the session holds no attr_encrypt key,
 * is not a delegate, or the fetch fails.
 */
export async function getOrgAttrKeyChain(): Promise<{ keyIndex: number; secretKey: Uint8Array }[]> {
    if (cachedChain) return cachedChain;

    const mySecretKey = keys.getPrivateKey('attr_encrypt');
    if (!mySecretKey) return [];

    try {
        const wrappedKeys = await delegateApi.getWrappedOrgKeys();
        const attrKeys = wrappedKeys
            .filter(wk => wk.keyId === 'attr_encrypt')
            .sort((a, b) => a.keyIndex - b.keyIndex);

        const chain: { keyIndex: number; secretKey: Uint8Array }[] = [];
        for (const wk of attrKeys) {
            const privKeyB64 = await unwrapPrivateKey(wk.wrappedKey, aes.fromBase64(mySecretKey));
            chain.push({ keyIndex: wk.keyIndex, secretKey: aes.fromBase64(privKeyB64) });
        }
        cachedChain = chain;
        return chain;
    } catch {
        return [];
    }
}

/**
 * The org's ORIGINAL (keyIndex 0) `attr_encrypt` private key, base64 — the key
 * a single-layer claim key was wrapped with. Returns null when unavailable.
 *
 * The sort matters: `/api/delegate/wrappedkeys` returns rows in no guaranteed
 * order, so picking "the first attr_encrypt entry" silently yields a
 * post-rotation key once an org has rotated, and every single-layer decrypt
 * then fails. Always index 0, explicitly.
 */
export async function getOrgAttrKey(): Promise<string | null> {
    const chain = await getOrgAttrKeyChain();
    const original = chain.find(k => k.keyIndex === 0);
    return original ? aes.toBase64(original.secretKey) : null;
}

/** Drop the cached chain — call on logout or identity switch. */
export function resetOrgKeyCache(): void {
    cachedChain = null;
}
